import React from 'react';
import { RouteComponentProps } from 'react-router';
import Styled from 'styled-components';
import {
  DecorativeElement,
  Headline,
  UploadBrowse,
  UploadBrowseImg,
  UploadBrowseLabel,
  UploadTap,
  UploadTapImg,
  UploadTapLabel,
  Upload,
} from '@amount/frontend-components';

import { IUUIDRouteProps } from '../../../constants';
import SessionManager from '../../../util/sessionManager';
import { MitekUpload, DocumentType } from '@Util/mitek';
import { IVerificationsUploadedFile } from '../../../gql';

export const ACCEPTABLE_FILE_FORMATS_STRING: string =
  '.pdf, .doc, .docx, .jpg, .jpeg, .png, .gif, .tiff, .bmp';
export const ACCEPTABLE_IMAGE_FORMATS_STRING: string = '.png, .jpg, .svg';

export const wasSuccessfulUpload = (upload: UploadFileResponse): upload is WithFileName<IUploadFileSuccessResponse> => !!upload.success;

export const generateVoidedCheckModalDescription: (bankName?: string | null) => string = bankName =>
  `Please upload a scanned or photographed digital image of a voided check from your${!!bankName ? ` ${bankName} ` : ' '}bank account.`;

export const promisedBase64Convert: (file: File) => Promise<string> = async file => {
  const fileReader: FileReader = new FileReader();
  fileReader.readAsDataURL(file);

  return new Promise<string>((resolve, reject) => {
    // eslint-disable-next-line  no-void
    fileReader.onload = () => { fileReader.result ? resolve(fileReader.result.toString()) : reject(); };
    fileReader.onerror = reject;
  });
};

export type WithFileName<T> = T & { fileName: string };

export interface IUploadFileSuccessResponse {
  refreshToken: string;
  success: true;
  attachmentUrl: string;
}
interface IUploadFileFailureResponse {
  success?: false;
  error?: string;
}

export type UploadFileResponse = WithFileName<
  IUploadFileFailureResponse | IUploadFileSuccessResponse
>;

export const tryToUnwrapJson: (
  response: Response
) => Promise<UploadFileResponse> = async response => {
  try {
    const unwrapped = await response.json();

    return { ...unwrapped };
  } catch (e) {
    return { success: false, error: response.statusText };
  }
};

export const uploadFile: (
  file: IFileWithPreview,
  title: string,
  customerApplicationUUID: string
) => Promise<UploadFileResponse> = async (file, title, customerApplicationUUID) => {
  const imgData: string = await promisedBase64Convert(file);
  const params = {
    imageDataBase64: imgData,
    filename: file.name,
    title,
    description: title,
    cropped: false,
    customerApplicationUUID
  };

  const response = await fetch(SessionManager.urlRouter.fileUploadUrl, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
      'X-Avant-Token': SessionManager.token || ''
    }
  });

  const json: UploadFileResponse = await tryToUnwrapJson(response);

  return {
    ...json,
    fileName: file.name
  };
};

export interface IViewSpecificProps {
  openFileDialog: React.MouseEventHandler<
    HTMLButtonElement | HTMLDivElement | HTMLSpanElement
  >;
}

export const isDomNodeVisible: (node: HTMLElement) => boolean = node => !!node.offsetParent;

export const Mobile: React.FunctionComponent<IViewSpecificProps> = ({
  openFileDialog
}) => (
  <UploadTap>
    <UploadTapLabel
      onClick={openFileDialog}
      role='button'
    >
      Choose a file
    </UploadTapLabel>
    <UploadTapImg>
      <DecorativeElement image='upload-tap' alt='Tap from device' />
    </UploadTapImg>
    <Headline scale='medium' as='p'>
      Tap to take a photo or upload from camera roll
    </Headline>
    <UploadBrowseLabel
      buttonStyle='secondary'
      inline={true}
      onClick={openFileDialog}
      data-event='browseFromDeviceMobile'
    >
      Browse from Device
    </UploadBrowseLabel>
  </UploadTap>
);

export const Desktop: React.FunctionComponent<IViewSpecificProps> = ({
  openFileDialog
}) => (
  <UploadBrowse>
    <UploadBrowseImg>
      {AvantConfig.TenantConfig.tasks.upload.showImage && (
        <DecorativeElement
          image='upload-browse'
          alt=''
        />
      )}
    </UploadBrowseImg>
    <Headline scale='medium' as='p'>
      Drag file onto this screen or
    </Headline>
    <UploadBrowseLabel
      buttonStyle='secondary'
      inline={true}
      onClick={openFileDialog}
      data-event='browseFromDeviceDesktop'
    >
      Browse from Device
    </UploadBrowseLabel>
    {AvantConfig.TenantConfig.tasks.upload.showFileFormats && (
      <small style={{ marginBottom: 0 }}>
        Acceptable file formats include {ACCEPTABLE_FILE_FORMATS_STRING}
      </small>
    )}
  </UploadBrowse>
);

const DOCX_MIME: string =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DOC_MIME: string = 'application/msword';

export const ACCEPT_MIMES: string = [
  'image/*',
  DOCX_MIME,
  DOC_MIME,
  'application/pdf',
  'application/x-pdf'
].join(',');

export interface IDropBasePassedProps {
  customerApplicationUUID: string;
  legacyUploadTask: boolean;
  uploadedFiles?: IVerificationsUploadedFile[];
  uploadType: string;
  uploadImagesToMitek?: MitekUpload;
  documentType?: DocumentType;
  backLink?: string;
  redirectOverride?: string;
  uploadFiles? (images: string[]): void;
  onFailure? (feedback: string): void;
  onFileDrop? (): void;
  onSuccess? (): void;
}

export interface IDropBaseProps
  extends IDropBasePassedProps,
  RouteComponentProps<IUUIDRouteProps> { }

export const DropzoneActiveClass: string = 'dropzoneActive';

export interface IDropStateBase {
  preview?: string;
  previews: Map<string, IFileWithPreview>;
  uploading: boolean;
  showUploadErrorModal: boolean;
  showInvalidTypeModal: boolean;
}

export interface IFileWithPreview extends File {
  preview?: string;
}

export const FileListDropEnabledUpload = Styled(Upload)`
  display: contents;
`;
export interface IFileProps {
  preview: string | undefined;
  showImagePreview?: boolean;
  index: number;
  name: string;
  removeImage: React.MouseEventHandler<HTMLElement>;
}

export interface IFileList {
  legacyUploadTask: boolean;
  files: Map<string, IFileWithPreview>;
  uploadedFiles?: IVerificationsUploadedFile[];
  removeImage: React.MouseEventHandler<HTMLElement>;
  openFileDialog: React.MouseEventHandler<HTMLElement>;
  hideAddAnotherButton?: boolean;
  showImagePreview?: boolean;
}
